import React, { useState } from 'react'
import { Observer } from 'mobx-react'
import { runInAction } from 'mobx'
import _get from 'lodash/get'
import _orderBy from 'lodash/orderBy'
import moment from 'moment-timezone'
import { Link } from '@material-ui/core'

import CircularProgress from '@material-ui/core/CircularProgress'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { ChartCard } from '../ChartCard'
import { LinearProgress } from '../LinearProgress'

import { useStores } from 'config/store'
import { dateFormat } from '../../../config/callpro.config'
import { CallDashboardTabs, CallOutcome } from 'interfaces/Call.interface'
import { ProductStore } from 'config/store/CallPro/Product/ProductStore'

import { useStyles } from './Services.style'
import CustomTooltip from '../CustomTooltip/CustomTooltip'
import AllServiceDrawer from 'components/CallPro/SideDrawer/AllService/AllServiceDrawer'
import CallDetailsDrawer from 'components/CallPro/SideDrawer/CallDetailsDrawer/CallDetailsDrawer'
import EmptyBarChart from '../EmptyCharts/EmptyBarChart'

interface ServicesInterface {
  isAccordionContent?: boolean
}

type ServiceItem = ProductStore['productInquiriesBarChartData'][number]

// MAIN FUNCTION
const Services = (props: ServicesInterface) => {
  const { isAccordionContent } = props
  const classes = useStyles()
  const { productStore, dashboardUIStore, callsStore } = useStores()
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleToggleDrawer = () => setDrawerOpen((value) => !value)

  const handleItemClick = (item: ServiceItem) => () => {
    runInAction(() => {
      const tab = CallDashboardTabs.ALL_CALLS
      const outcome = CallOutcome.ALL_CALLS
      dashboardUIStore.setCurrentTabbedOpened(tab)
      callsStore.setFilters({ outcome, service: [item.id] })
      callsStore.setPaginationPage(1)
      callsStore.refreshAllCallsTab()
    })
  }

  // TOOLTIP CONTENT
  const tooltipContent = (
    <div className={classes.metricTooltip}>
      <div className={classes.tooltipTitle}>Click to see the full list</div>
    </div>
  )

  return (
    <Observer
      render={() => {
        const { productInquiriesBarChartData, productInquiriesLoading } =
          productStore

        const total = productInquiriesBarChartData.reduce(
          (accumulator, currentElement) => accumulator + currentElement.value,
          0
        )

        return (
          <>
            <ChartCard
              headerTitle="TOP REQUESTED SERVICES"
              tooltipText="The services or treatments requested or discussed during the call."
              headerTotalText="Total Calls"
              headerTotalValue={total}
              isAccordionContent={isAccordionContent}
            >
              {productInquiriesLoading ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '2rem',
                  }}
                >
                  <CircularProgress />
                </div>
              ) : productInquiriesBarChartData.length > 0 ? (
                <>
                  {productInquiriesBarChartData
                    .slice(0, 5)
                    .map((productInquiry, key) => {
                      return (
                        <LinearProgress
                          key={`item-${key}`}
                          value={productInquiry.value}
                          total={total}
                          label={
                            <Link
                              component="button"
                              variant="body1"
                              underline="none"
                              color="initial"
                              onClick={handleItemClick(productInquiry)}
                              classes={{ root: classes.link }}
                            >
                              {productInquiry.name}
                            </Link>
                          }
                        />
                      )
                    })}
                  <div className={classes.footer}>
                    <div className={classes.container}>
                      <div className={classes.infoContainer}>
                        <div className={classes.info}>
                          <CustomTooltip content={tooltipContent}>
                            <div
                              className={classes.infoDetail}
                              onClick={handleToggleDrawer}
                            >
                              All Services (
                              {productInquiriesBarChartData.length})
                            </div>
                          </CustomTooltip>
                        </div>
                      </div>
                      {productStore.productStart && (
                        <div className={classes.disclaimer}>
                          <InfoOutlinedIcon
                            className={classes.disclaimerIcon}
                          />
                          The data used in this graph is derived from the calls
                          we scored since{' '}
                          {moment(productStore.productStart).format(dateFormat)}
                          .
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <EmptyBarChart />
              )}
            </ChartCard>
            <CallDetailsDrawer
              drawerOpen={drawerOpen}
              handleCloseDrawer={handleToggleDrawer}
              title="All Requested Services"
            >
              <AllServiceDrawer />
            </CallDetailsDrawer>
          </>
        )
      }}
    />
  )
}

export default Services
